// Daymio Trends — player statistical-delays page.
// "Ritardi statistici" su 7 stat (tiri, tiri in porta, gialli, falli,
// falli subiti, gol, graziati). UI pensata per far capire al volo
// quali ritardi sono "appetitosi" (alto delay_ratio + alto appetite score).

import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { playSfx } from "@/lib/audio";
import { staggerReveal, tabSlide } from "@/lib/motion";
import Counter from "@/components/Counter";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, AlertTriangle, Clock, Flame, Target, Activity,
  TrendingUp, Loader2, Filter, Calendar, Award, ChevronUp, ChevronDown,
  RefreshCw, Search, X,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { usePremium } from "@/contexts/AuthContext";
import PremiumGate from "@/components/PremiumGate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read auth credentials from AccessGate / admin session — needed by the
// "🔄 Aggiorna ora" button which talks to /api/daymio/refresh.
function readAuthCreds() {
  try {
    const adm = JSON.parse(localStorage.getItem("bc_admin_session") || "null");
    if (adm?.token) return { admin_token: adm.token };
  } catch { /* ignore */ }
  try {
    const sess = JSON.parse(localStorage.getItem("bc_access_session") || "null");
    if (sess?.code) return { code: sess.code };
  } catch { /* ignore */ }
  return {};
}

const STATS = [
  { id: "shots",       label: "Tiri",         icon: Target,     color: "#22D3EE" },
  { id: "shots_on",    label: "Tiri in porta",icon: Target,     color: "#A78BFA" },
  { id: "yellow",      label: "Cartellini",   icon: AlertTriangle, color: "#F59E0B" },
  { id: "foul",        label: "Falli",        icon: Activity,   color: "#EF4444" },
  { id: "foul_drawn",  label: "Falli subiti", icon: Activity,   color: "#10B981" },
  { id: "goal",        label: "Gol",          icon: Award,      color: "#22C55E" },
  { id: "graziati",    label: "Graziati",     icon: Flame,      color: "#F97316" },
];

const DEFAULT_FILTERS = {
  shots:      { minMin: 90,  minAvg: 1.5, mult: 0 },
  shots_on:   { minMin: 90,  minAvg: 0.5, mult: 0 },
  yellow:     { minMin: 200, minAvg: 0.2, mult: 0 },
  foul:       { minMin: 90,  minAvg: 1.0, mult: 0 },
  foul_drawn: { minMin: 90,  minAvg: 1.0, mult: 0 },
  goal:       { minMin: 180, minAvg: 0.2, mult: 0 },
  graziati:   { minMin: 200, minAvg: 0.5, mult: 0 },  // avg = falli/90min
};

// Per-stat slider ranges (min/max/step). All averages are now per 90 min.
const SLIDER_RANGES = {
  shots:      { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 5, step: 0.1 } },
  shots_on:   { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 3, step: 0.05 } },
  yellow:     { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 1, step: 0.05 } },
  foul:       { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 5, step: 0.1 } },
  foul_drawn: { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 5, step: 0.1 } },
  goal:       { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 2, step: 0.05 } },
  graziati:   { min: { lo: 0, hi: 1500, step: 10 }, avg: { lo: 0, hi: 5, step: 0.05 } }, // falli/90min
};

// Avg-column label per stat (always "X / 90 min" but with sport-correct noun).
const AVG_COL_LABEL = {
  shots:      "Tiri / 90 min",
  shots_on:   "Tiri P. / 90 min",
  yellow:     "Gialli / 90 min",
  foul:       "Falli / 90 min",
  foul_drawn: "Falli sub. / 90 min",
  goal:       "Gol / 90 min",
  graziati:   "Falli / 90 min",
};

const SLIDER_LABEL = {
  shots:      "Media tiri per 90 min",
  shots_on:   "Media tiri in porta per 90 min",
  yellow:     "Media gialli per 90 min",
  foul:       "Media falli per 90 min",
  foul_drawn: "Media falli subiti per 90 min",
  goal:       "Media gol per 90 min",
  graziati:   "Media falli per 90 min",
};

function appetiteColor(score) {
  // 0 → grey, 50 → orange, 100 → red-hot
  if (score >= 80) return { bg: "rgba(239,68,68,0.15)", fg: "#EF4444", label: "🔥 BIG" };
  if (score >= 60) return { bg: "rgba(249,115,22,0.15)", fg: "#F97316", label: "🔥 HOT" };
  if (score >= 40) return { bg: "rgba(245,158,11,0.12)", fg: "#F59E0B", label: "✨ OK" };
  return { bg: "rgba(113,113,122,0.10)", fg: "#71717A", label: "—" };
}

export default function DaymioTrendsPage() {
  const navigate = useNavigate();
  const { isPremium, loading: premiumLoading } = usePremium();

  // Hard gate: Free users see only a Premium paywall (no data fetched).
  if (!premiumLoading && !isPremium) {
    return (
      <div style={{ minHeight: "100vh", background: "#020202", color: "#fff" }}>
        <Navbar />
        <div style={{ maxWidth: 720, margin: "0 auto", padding: "3rem 1.5rem" }}>
          <button
            data-testid="daymio-back-btn"
            onClick={() => navigate("/")}
            style={{
              background: "transparent", border: "1px solid rgba(255,255,255,0.1)",
              color: "#71717A", padding: "0.45rem 0.85rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.7rem",
              letterSpacing: "0.16em", textTransform: "uppercase", cursor: "pointer",
              marginBottom: "1.5rem", display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <ArrowLeft size={12} /> Torna alla Home
          </button>
          <PremiumGate
            feature="Daymio Trends"
            reason="Ritardi statistici giocatori · 7 stat · Appetite score"
            testid="premium-gate-daymio"
            style={{ minHeight: 280 }}
          >
            <div style={{
              padding: "3rem 2rem",
              background: "rgba(255,255,255,0.02)",
              border: "1px solid rgba(255,255,255,0.06)",
              textAlign: "center",
            }}>
              <Flame size={32} style={{ color: "#F59E0B", marginBottom: 12 }} />
              <h2 style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
                fontSize: "1.8rem", letterSpacing: "0.06em", textTransform: "uppercase",
              }}>
                Daymio · Ritardi Giocatori
              </h2>
              <p style={{ color: "#71717A", marginTop: 8 }}>
                Identifica giocatori in ritardo statistico su tiri, cartellini, falli, gol e altro.
                Filtri custom, soglia appetite, alert real-time.
              </p>
            </div>
          </PremiumGate>
        </div>
      </div>
    );
  }

  return <DaymioTrendsPageInner />;
}

function DaymioTrendsPageInner() {
  const navigate = useNavigate();
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(135);
  const [stat, setStat] = useState("graziati");
  const [filters, setFilters] = useState(DEFAULT_FILTERS["graziati"]);
  const [onlyToday, setOnlyToday] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [detailPlayer, setDetailPlayer] = useState(null);
  const [detailData, setDetailData] = useState(null);
  // Sortable columns: minutes_since_last | avg_per_match | delay_ratio | appetite
  // Default: graziati → avg_per_match desc; other stats → appetite desc.
  const [sortKey, setSortKey] = useState("appetite");
  const [sortDir, setSortDir] = useState("desc"); // "asc" | "desc"
  // Manual refresh state — wired to /api/daymio/refresh button
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null); // {type:"ok"|"err", text:string}
  const [lastSync, setLastSync] = useState(null); // ISO string of last global sync
  // Free-text team filter — matches the player's team or next-fixture opponent
  const [teamFilter, setTeamFilter] = useState("");

  // Fetch leagues with auto-retry — robust against transient backend
  // restarts / network blips during initial page load.
  const fetchLeagues = async (attempt = 0) => {
    try {
      const r = await axios.get(`${API}/daymio/leagues`);
      setLeagues(r.data?.leagues || []);
    } catch (e) {
      if (attempt < 3) {
        setTimeout(() => fetchLeagues(attempt + 1), 1500 * (attempt + 1));
      }
    }
  };
  useEffect(() => { fetchLeagues(); /* eslint-disable-next-line */ }, []);

  // Fetch the latest sync timestamp shown next to the refresh button.
  const fetchLastSync = async () => {
    try {
      const r = await axios.get(`${API}/daymio/sync-status`);
      const ts = r.data?.last_global_run?.finished_at || r.data?.last_global_run?.started_at;
      setLastSync(ts || null);
    } catch { /* silent */ }
  };
  useEffect(() => { fetchLastSync(); /* eslint-disable-next-line */ }, []);

  // Manual refresh — calls /api/daymio/refresh with admin_token (if admin)
  // or the user's BCRK access code, then reloads streaks + sync timestamp.
  const handleRefresh = async () => {
    if (refreshing) return;
    const creds = readAuthCreds();
    if (!creds.admin_token && !creds.code) {
      setRefreshMsg({ type: "err", text: "Sessione scaduta — effettua di nuovo il login." });
      setTimeout(() => setRefreshMsg(null), 4000);
      return;
    }
    setRefreshing(true); setRefreshMsg(null);
    try { playSfx?.("click"); } catch { /* ignore */ }
    try {
      const payload = { ...creds };
      if (leagueId) payload.league_id = leagueId; // refresh only current league when set
      const r = await axios.post(`${API}/daymio/refresh`, payload, { timeout: 90_000 });
      const newRows = r.data?.total_rows ?? r.data?.rows_written ?? 0;
      const newFx = r.data?.total_new_fixtures ?? r.data?.new_fixtures ?? 0;
      setRefreshMsg({ type: "ok", text: `✓ ${newFx} nuove partite, ${newRows} righe aggiornate` });
      await fetchLastSync();
      await load(); // reload streaks
      try { playSfx?.("coin"); } catch { /* ignore */ }
    } catch (e) {
      const status = e?.response?.status;
      const det = e?.response?.data?.detail;
      let msg = "Errore durante l'aggiornamento — riprova tra qualche secondo.";
      if (status === 429) {
        const retry = e?.response?.data?.detail?.retry_after_seconds;
        msg = retry ? `Attendi ${retry}s prima di un nuovo refresh` : "Troppi refresh — riprova tra poco";
      } else if (typeof det === "string") msg = det;
      setRefreshMsg({ type: "err", text: msg });
    } finally {
      setRefreshing(false);
      setTimeout(() => setRefreshMsg(null), 5000);
    }
  };

  // Format last-sync timestamp in Europe/Rome local time (HH:MM, dd/mm).
  const lastSyncLabel = useMemo(() => {
    if (!lastSync) return null;
    try {
      const d = new Date(lastSync);
      const time = d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" });
      const day = d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", timeZone: "Europe/Rome" });
      return `${time} · ${day}`;
    } catch { return null; }
  }, [lastSync]);

  // Load defaults each time the stat changes
  useEffect(() => {
    setFilters(DEFAULT_FILTERS[stat] || DEFAULT_FILTERS.graziati);
    // Reset default sort: graziati → avg_per_match (matches the "fall-y" leaderboard
    // pattern of the reference software); other stats → appetite (composite).
    setSortKey(stat === "graziati" ? "avg_per_match" : "appetite");
    setSortDir("desc");
  }, [stat]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sortedResults = useMemo(() => {
    const list = data?.results ? [...data.results] : [];
    const q = teamFilter.trim().toLowerCase();
    const filtered = q
      ? list.filter((r) => {
          const team = (r.team_name || "").toLowerCase();
          const home = (r.next_fixture?.home || "").toLowerCase();
          const away = (r.next_fixture?.away || "").toLowerCase();
          return team.includes(q) || home.includes(q) || away.includes(q);
        })
      : list;
    filtered.sort((a, b) => {
      const av = a?.[sortKey] ?? 0;
      const bv = b?.[sortKey] ?? 0;
      return sortDir === "desc" ? bv - av : av - bv;
    });
    return filtered;
  }, [data, sortKey, sortDir, teamFilter]);

  const load = async (attempt = 0) => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/daymio/streaks`, {
        params: {
          league: leagueId, stat,
          min_minutes_since: filters.minMin,
          min_avg_per_match: filters.minAvg,
          delay_multiplier: filters.mult,
          only_today: onlyToday,
          limit: 50,
        },
      });
      setData(r.data);
    } catch (e) {
      // Auto-retry once on transient errors (404/500/network) — covers
      // the brief restart window where the backend isn't ready yet.
      const status = e?.response?.status;
      const transient = !status || status === 404 || status === 502 || status === 503;
      if (transient && attempt === 0) {
        setTimeout(() => load(1), 1800);
        return;
      }
      const msg = e?.response?.data?.detail
        || (status === 404 ? "Server momentaneamente non disponibile — riprova tra qualche secondo."
        :   "Errore caricamento dati. Verifica la connessione e riprova.");
      setErr(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leagueId, stat, onlyToday]);

  const handleApply = () => load();

  const handleDetails = async (p) => {
    setDetailPlayer(p); setDetailData(null);
    try {
      const r = await axios.get(`${API}/daymio/player/${p.player_id}`, { params: { season: p.season } });
      setDetailData(r.data);
    } catch { setDetailData({ error: true }); }
  };

  const statMeta = STATS.find((s) => s.id === stat) || STATS[0];

  // Stagger reveal whenever the sorted result list changes (filter, sort, refresh)
  const tbodyRef = useRef(null);
  // Refs for the iOS-style stat-tabs sliding highlight
  const tabsContainerRef = useRef(null);
  const tabHighlightRef = useRef(null);
  const tabRefs = useRef({});
  useEffect(() => {
    if (!tbodyRef.current || !sortedResults.length) return;
    staggerReveal(tbodyRef.current.querySelectorAll("tr"), {
      from: 10, scale: 0.998, duration: 460, delayBetween: 32, baseDelay: 50,
    });
  }, [sortedResults]);

  // Slide the underline-highlight under the active stat tab
  useEffect(() => {
    const t = tabRefs.current[stat];
    if (!t) return;
    tabSlide(tabHighlightRef.current, t);
  }, [stat]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bc-page-bg, var(--bc-panel, #09090B))", color: "var(--bc-text, #FAFAFA)" }}>
      <Navbar />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "1.5rem 1rem" }}>

        {/* HEADER */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1.5rem", flexWrap: "wrap", gap: "1rem" }}>
          <div>
            <button onClick={() => navigate(-1)} data-testid="daymio-back-btn"
              style={{ display: "flex", alignItems: "center", gap: 6, color: "rgba(255,255,255,0.5)", fontSize: "0.7rem", marginBottom: 6 }}>
              <ArrowLeft size={14} /> Indietro
            </button>
            <h1 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "2.4rem", fontWeight: 800, letterSpacing: "0.04em", color: "var(--bc-accent, #F59E0B)" }}>
              DAYMIO TRENDS
            </h1>
            <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.85rem" }}>
              Ritardi statistici dei giocatori — più alto è l'<span style={{ color: "#F97316", fontWeight: 700 }}>appetite</span>, più "in ritardo" è la pick.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            <select value={leagueId} onChange={(e) => setLeagueId(Number(e.target.value))} data-testid="daymio-league-select"
              style={{ background: "var(--bc-panel, #18181B)", color: "var(--bc-ink, #FAFAFA)", border: "1px solid #27272A", padding: "0.5rem 0.7rem", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem" }}>
              <option value={0}>Tutti i campionati</option>
              {leagues.map((L) => <option key={L.id} value={L.id}>{L.name}</option>)}
            </select>
            <button data-testid="daymio-only-today" onClick={() => setOnlyToday((v) => !v)}
              style={{
                background: onlyToday ? "var(--bc-accent, #F59E0B)" : "#18181B",
                color: onlyToday ? "#000" : "#FAFAFA",
                border: `1px solid ${onlyToday ? "var(--bc-accent, #F59E0B)" : "#27272A"}`,
                padding: "0.5rem 0.85rem", fontSize: "0.7rem", letterSpacing: "0.12em",
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, textTransform: "uppercase",
                display: "flex", alignItems: "center", gap: 6,
              }}>
              <Calendar size={12} /> Solo oggi
            </button>
            <button data-testid="daymio-refresh-btn" onClick={handleRefresh} disabled={refreshing}
              title={leagueId
                ? `Aggiorna i dati di ${leagues.find(L => L.id === leagueId)?.name || "questa lega"}`
                : "Aggiorna i dati di tutte le leghe (~60s)"}
              style={{
                background: refreshing ? "rgba(34,211,238,0.15)" : "#18181B",
                color: refreshing ? "#22D3EE" : "#FAFAFA",
                border: `1px solid ${refreshing ? "#22D3EE" : "#27272A"}`,
                padding: "0.5rem 0.85rem", fontSize: "0.7rem", letterSpacing: "0.12em",
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700, textTransform: "uppercase",
                display: "flex", alignItems: "center", gap: 6,
                cursor: refreshing ? "wait" : "pointer",
                boxShadow: refreshing ? "0 0 12px rgba(34,211,238,0.35)" : "none",
                transition: "box-shadow 0.25s, border-color 0.25s",
              }}>
              <RefreshCw size={12} style={{ animation: refreshing ? "spin 1s linear infinite" : "none" }} />
              {refreshing ? "Sync…" : "Aggiorna ora"}
            </button>
          </div>
        </div>
        {/* Refresh status row: last-sync timestamp + transient toast */}
        {(lastSyncLabel || refreshMsg) && (
          <div data-testid="daymio-sync-status" style={{
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
            marginTop: "-0.5rem", marginBottom: "1rem", fontSize: "0.7rem",
            fontFamily: "'JetBrains Mono',monospace", color: "rgba(255,255,255,0.45)",
          }}>
            {lastSyncLabel && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Clock size={11} /> Ultimo sync: <span style={{ color: "rgba(255,255,255,0.75)" }}>{lastSyncLabel}</span>
              </span>
            )}
            {refreshMsg && (
              <span data-testid="daymio-refresh-msg" style={{
                color: refreshMsg.type === "ok" ? "#22C55E" : "#F87171",
                fontWeight: 600, letterSpacing: "0.02em",
              }}>
                {refreshMsg.text}
              </span>
            )}
          </div>
        )}

        {/* STAT TABS — iOS-style sliding highlight */}
        <div data-testid="daymio-stat-tabs" ref={tabsContainerRef}
          style={{ display: "flex", gap: 4, marginBottom: "1rem", overflowX: "auto", paddingBottom: 4, position: "relative" }}>
          {/* Sliding highlight (positioned absolutely behind the active tab) */}
          <div ref={tabHighlightRef} aria-hidden style={{
            position: "absolute", top: 0, left: 0, height: "100%",
            background: `color-mix(in srgb, ${statMeta.color} 16%, var(--bc-panel, #09090B))`,
            border: `1px solid ${statMeta.color}`,
            boxShadow: `0 0 16px -4px ${statMeta.color}, inset 0 0 14px -8px ${statMeta.color}`,
            pointerEvents: "none", zIndex: 0, willChange: "transform, width",
            transition: "background 0.3s, border-color 0.3s, box-shadow 0.3s",
          }} />
          {STATS.map((s) => {
            const active = s.id === stat;
            const Icon = s.icon;
            return (
              <button key={s.id} data-testid={`daymio-tab-${s.id}`}
                ref={(el) => { tabRefs.current[s.id] = el; }}
                onClick={() => { playSfx("click"); setStat(s.id); }}
                style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "0.55rem 0.9rem",
                  background: "transparent",
                  border: "1px solid transparent",
                  color: active ? s.color : "rgba(255,255,255,0.55)",
                  fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.82rem",
                  letterSpacing: "0.18em", textTransform: "uppercase", fontWeight: 700,
                  whiteSpace: "nowrap", cursor: "pointer",
                  position: "relative", zIndex: 1,
                  transition: "color 0.3s ease",
                }}>
                <Icon size={13} /> {s.label}
              </button>
            );
          })}
        </div>

        {/* FILTERS */}
        <div data-testid="daymio-filters" style={{
          background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", border: "1px solid #27272A",
          padding: "1rem", marginBottom: "1rem",
          display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "1.25rem",
        }}>
          <SliderField
            label="Minuti dall'ultimo evento"
            value={filters.minMin}
            onChange={(v) => setFilters({ ...filters, minMin: v })}
            min={SLIDER_RANGES[stat].min.lo}
            max={SLIDER_RANGES[stat].min.hi}
            step={SLIDER_RANGES[stat].min.step}
            color={statMeta.color}
            unit="min"
          />
          <SliderField
            label={SLIDER_LABEL[stat] || "Media per 90 min"}
            value={filters.minAvg}
            onChange={(v) => setFilters({ ...filters, minAvg: Number(v.toFixed(2)) })}
            min={SLIDER_RANGES[stat].avg.lo}
            max={SLIDER_RANGES[stat].avg.hi}
            step={SLIDER_RANGES[stat].avg.step}
            color={statMeta.color}
            unit=""
            decimals={2}
          />
          <SelectField label="Ritardo (delay × media)"
            value={filters.mult} onChange={(v) => setFilters({ ...filters, mult: v })}
            options={[{v:0,l:"0× (qualsiasi)"},{v:1,l:"1× (in media)"},{v:1.5,l:"1.5× (in ritardo)"},{v:2,l:"2× (forte)"},{v:3,l:"3× (raro)"}]} />
          {/* Team text filter — applied client-side over team_name + next-fixture opponent */}
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.7rem",
              letterSpacing: "0.16em", textTransform: "uppercase",
              color: "rgba(255,255,255,0.5)",
            }}>
              Filtra squadra
            </label>
            <div style={{ position: "relative", width: "100%" }}>
              <Search size={12} style={{
                position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                color: "rgba(255,255,255,0.35)", pointerEvents: "none",
              }} />
              <input
                type="text"
                value={teamFilter}
                onChange={(e) => setTeamFilter(e.target.value)}
                placeholder="es. Inter, Real, Bayern…"
                data-testid="daymio-team-filter"
                style={{
                  width: "100%", background: "var(--bc-panel, var(--bc-panel, #0A0A0A))", border: "1px solid #27272A",
                  color: "var(--bc-ink, #FAFAFA)", padding: "0.5rem 2rem 0.5rem 1.95rem",
                  fontFamily: "'JetBrains Mono',monospace", fontSize: "0.85rem",
                  outline: "none", transition: "border-color 0.15s, box-shadow 0.15s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = statMeta.color;
                  e.target.style.boxShadow = `0 0 8px -2px ${statMeta.color}`;
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#27272A";
                  e.target.style.boxShadow = "none";
                }}
              />
              {teamFilter && (
                <button
                  type="button"
                  onClick={() => setTeamFilter("")}
                  data-testid="daymio-team-filter-clear"
                  aria-label="Pulisci filtro squadra"
                  style={{
                    position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                    width: 20, height: 20, display: "inline-flex", alignItems: "center",
                    justifyContent: "center", background: "transparent", border: "none",
                    color: "rgba(255,255,255,0.55)", cursor: "pointer",
                  }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          </div>
          <button onClick={handleApply} data-testid="daymio-apply-btn"
            style={{
              alignSelf: "flex-end", padding: "0.55rem 1rem",
              background: statMeta.color, color: "#000",
              border: "none", fontFamily: "'Barlow Condensed',sans-serif",
              fontSize: "0.85rem", letterSpacing: "0.18em", fontWeight: 800,
              textTransform: "uppercase", cursor: "pointer",
            }}>
            <Filter size={12} style={{ marginRight: 6, display: "inline" }} /> Applica filtri
          </button>
        </div>

        {/* RESULTS */}
        {loading && <div style={{ textAlign: "center", padding: "3rem", color: "rgba(255,255,255,0.5)" }}>
          <Loader2 className="animate-spin" size={32} /> Calcolo ritardi…
        </div>}
        {err && (
          <div data-testid="daymio-error" style={{
            background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.5)",
            padding: "1rem", color: "#FCA5A5", display: "flex", alignItems: "center",
            justifyContent: "space-between", gap: "1rem", flexWrap: "wrap",
          }}>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem" }}>{err}</span>
            <button onClick={() => { fetchLeagues(); load(); }} data-testid="daymio-retry-btn"
              style={{
                background: "transparent", border: "1px solid #FCA5A5", color: "#FCA5A5",
                padding: "0.4rem 0.85rem", fontFamily: "'Barlow Condensed',sans-serif",
                fontSize: "0.72rem", letterSpacing: "0.16em", textTransform: "uppercase",
                fontWeight: 700, cursor: "pointer",
              }}>
              Riprova
            </button>
          </div>
        )}
        {!loading && data && sortedResults.length === 0 && (
          <div style={{ textAlign: "center", padding: "3rem", color: "rgba(255,255,255,0.5)" }}>
            <Clock size={32} style={{ marginBottom: 12 }} />
            <p style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "1.1rem", letterSpacing: "0.1em" }}>
              Nessun giocatore trovato con questi filtri.
            </p>
            <p style={{ fontSize: "0.8rem", marginTop: 6 }}>
              {teamFilter
                ? <>Nessuna corrispondenza per "<span style={{ color: statMeta.color }}>{teamFilter}</span>" — prova un altro nome o pulisci il filtro.</>
                : onlyToday ? "Forse oggi non ci sono partite per questa lega — togli 'Solo oggi'." : "Prova ad abbassare i filtri (minuti, media, ritardo)."}
            </p>
          </div>
        )}
        {!loading && data && sortedResults.length > 0 && (
          <div data-testid="daymio-results-table" style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", border: "1px solid #27272A", overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem" }}>
              <thead>
                <tr style={{ background: "var(--bc-panel-elev, var(--bc-panel-elev, rgba(255,255,255,0.03)))", textAlign: "left", color: "rgba(255,255,255,0.5)", fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: "0.16em", textTransform: "uppercase", fontSize: "0.7rem" }}>
                  <th style={{ padding: "0.7rem 1rem" }}>Giocatore</th>
                  <th style={{ padding: "0.7rem", textAlign: "left" }}>Prossima partita</th>
                  <SortHeader label="Min. dall'ultimo" align="right" sortKey="minutes_since_last"
                    activeKey={sortKey} dir={sortDir} onSort={handleSort} color={statMeta.color} />
                  <SortHeader label={AVG_COL_LABEL[stat] || "Media / 90 min"} align="right" sortKey="avg_per_match"
                    activeKey={sortKey} dir={sortDir} onSort={handleSort} color={statMeta.color} />
                  <SortHeader label="Ritardo" align="right" sortKey="delay_ratio"
                    activeKey={sortKey} dir={sortDir} onSort={handleSort} color={statMeta.color} />
                  <SortHeader label="Appetite" align="center" sortKey="appetite"
                    activeKey={sortKey} dir={sortDir} onSort={handleSort} color={statMeta.color} />
                </tr>
              </thead>
              <tbody ref={tbodyRef}>
                {sortedResults.map((r) => {
                  const a = appetiteColor(r.appetite);
                  return (
                    <tr key={r.player_id} onClick={() => handleDetails(r)} data-testid={`daymio-row-${r.player_id}`}
                      style={{ borderTop: "1px solid var(--bc-border, rgba(255,255,255,0.07))", cursor: "pointer", transition: "background 0.15s", opacity: 0 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--bc-panel-elev, rgba(255,255,255,0.04))"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      <td style={{ padding: "0.65rem 1rem", fontWeight: 600, color: "var(--bc-ink, #FAFAFA)" }}>{r.player_name}</td>
                      <td style={{ padding: "0.65rem", textAlign: "left", color: "rgba(255,255,255,0.7)", fontSize: "0.72rem", lineHeight: 1.3 }}>
                        {r.next_fixture ? (
                          <div>
                            <div style={{ color: "var(--bc-ink, #FAFAFA)" }}>
                              <NextFixtureLabel teamName={r.team_name} nf={r.next_fixture} />
                            </div>
                            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.65rem", marginTop: 2 }}>
                              {r.next_fixture.date}{r.next_fixture.time ? ` · ${r.next_fixture.time}` : ""}
                              {r.next_fixture.league_name ? ` · ${r.next_fixture.league_name}` : ""}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: "rgba(255,255,255,0.35)" }}>{r.team_name} · nessuna prossima partita</span>
                        )}
                      </td>
                      <td style={{ padding: "0.65rem", textAlign: "right", color: statMeta.color, fontWeight: 700 }}>
                        <Counter value={r.minutes_since_last} duration={750} />
                      </td>
                      <td style={{ padding: "0.65rem", textAlign: "right", color: "rgba(255,255,255,0.7)" }}>
                        <Counter value={r.avg_per_match} decimals={2} duration={750} />
                      </td>
                      <td style={{ padding: "0.65rem", textAlign: "right", fontWeight: 700, color: r.delay_ratio >= 2 ? "#F97316" : "rgba(255,255,255,0.7)" }}>
                        <Counter value={r.delay_ratio} decimals={2} suffix="×" duration={750} />
                      </td>
                      <td style={{ padding: "0.65rem", textAlign: "center" }}>
                        <span style={{
                          padding: "0.2rem 0.55rem", background: a.bg, border: `1px solid ${a.fg}`,
                          color: a.fg, fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.72rem",
                          letterSpacing: "0.14em", fontWeight: 800, textTransform: "uppercase",
                        }}>
                          {a.label} <Counter value={r.appetite} duration={900} />
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DETAIL MODAL */}
      {detailPlayer && (
        <div onClick={() => { setDetailPlayer(null); setDetailData(null); }}
          style={{ position: "fixed", inset: 0, background: "var(--bc-panel, rgba(0,0,0,0.75))", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ background: "#0F0F11", border: `1px solid ${statMeta.color}`, maxWidth: 720, width: "100%", maxHeight: "85vh", overflowY: "auto", padding: "1.5rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem" }}>
              <div>
                <h3 style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "1.4rem", fontWeight: 800, color: statMeta.color }}>{detailPlayer.player_name}</h3>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem" }}>{detailPlayer.team_name} · {detailPlayer.league_name}</p>
              </div>
              <button onClick={() => { setDetailPlayer(null); setDetailData(null); }} style={{ color: "rgba(255,255,255,0.5)" }}>✕</button>
            </div>
            {!detailData && <Loader2 className="animate-spin" size={20} />}
            {detailData?.error && <p style={{ color: "#FCA5A5" }}>Storico non disponibile.</p>}
            {detailData?.matches && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.72rem", fontFamily: "'JetBrains Mono',monospace" }}>
                <thead style={{ color: "rgba(255,255,255,0.4)", textAlign: "left" }}>
                  <tr>
                    <th style={{ padding: "0.4rem" }}>Data</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>Min</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>Tiri</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>↗ Porta</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>Gialli</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>Falli</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>Subiti</th>
                    <th style={{ padding: "0.4rem", textAlign: "right" }}>Gol</th>
                  </tr>
                </thead>
                <tbody>
                  {detailData.matches.map((m) => (
                    <tr key={m.fixture_id} style={{ borderTop: "1px solid var(--bc-border, rgba(255,255,255,0.07))" }}>
                      <td style={{ padding: "0.4rem", color: "rgba(255,255,255,0.5)" }}>{m.date}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right" }}>{m.minutes}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right" }}>{m.shots}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right" }}>{m.shots_on}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right", color: m.yellows > 0 ? "#F59E0B" : "" }}>{m.yellows}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right" }}>{m.fouls}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right" }}>{m.fouls_drawn}</td>
                      <td style={{ padding: "0.4rem", textAlign: "right", color: m.goals > 0 ? "#22C55E" : "" }}>{m.goals}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SliderField({ label, value, onChange, min, max, step, color = "#22C55E", unit = "", decimals = 0 }) {
  const display = decimals > 0 ? Number(value).toFixed(decimals) : Math.round(value);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.7rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)" }}>{label}</label>
        <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.95rem", fontWeight: 700, color, textShadow: `0 0 8px ${color}66` }}>
          {display}{unit ? ` ${unit}` : ""}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => { onChange(Number(e.target.value)); if (Math.random() < 0.18) playSfx("tick"); }}
        data-testid={`daymio-slider-${label.replace(/\s+/g, "-").toLowerCase()}`}
        style={{
          width: "100%",
          accentColor: color,
          cursor: "pointer",
          height: 4,
        }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", color: "rgba(255,255,255,0.3)" }}>
        <span>{decimals > 0 ? min.toFixed(decimals) : min}</span>
        <span>{decimals > 0 ? max.toFixed(decimals) : max}</span>
      </div>
    </div>
  );
}

function NextFixtureLabel({ teamName, nf }) {
  const home = nf.home || "?";
  const away = nf.away || "?";
  const isHome = (teamName || "").toLowerCase() === home.toLowerCase();
  const me = isHome ? home : away;
  const opp = isHome ? away : home;
  return (
    <span>
      <span style={{ color: "var(--bc-ink, #FAFAFA)", fontWeight: 600 }}>{me}</span>
      <span style={{ color: "rgba(255,255,255,0.35)", margin: "0 6px" }}>{isHome ? "vs" : "@"}</span>
      <span style={{ color: "rgba(255,255,255,0.7)" }}>{opp}</span>
    </span>
  );
}

function SortHeader({ label, sortKey, activeKey, dir, onSort, align = "right", color = "#22C55E" }) {
  const active = sortKey === activeKey;
  const Icon = active && dir === "asc" ? ChevronUp : ChevronDown;
  return (
    <th
      onClick={() => onSort(sortKey)}
      data-testid={`daymio-sort-${sortKey}`}
      style={{
        padding: "0.7rem", textAlign: align, cursor: "pointer", userSelect: "none",
        color: active ? color : "rgba(255,255,255,0.5)",
        textShadow: active ? `0 0 8px ${color}66` : "none",
        transition: "color 0.15s",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, justifyContent: align === "center" ? "center" : "flex-end" }}>
        {label}
        <Icon size={12} style={{ opacity: active ? 1 : 0.25, marginTop: 1 }} />
      </span>
    </th>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label style={{ display: "block", fontFamily: "'Barlow Condensed',sans-serif", fontSize: "0.7rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>{label}</label>
      <select value={value} onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`daymio-select-${label.replace(/\s+/g, "-").toLowerCase()}`}
        style={{ width: "100%", background: "var(--bc-panel, var(--bc-panel, #0A0A0A))", border: "1px solid #27272A", color: "var(--bc-ink, #FAFAFA)", padding: "0.5rem 0.6rem", fontFamily: "'JetBrains Mono',monospace", fontSize: "0.85rem" }}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}
